import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import { ConfirmPopover } from '../../library/ConfirmPopover'
import { ListLoading } from '../../library/ListLoading'
import { Sentinel } from '../../library/Sentinel'
import type { OfApi } from '../ofApi'
import { QueueCard, type QueueActions } from './QueueItem'
import { LoadFailed } from './QueueViews'
import { LATER_REFERER, toAutoPublish, type AutoPublish, type LaterKind, type ScheduleItem } from './queueData'

/** Saved-for-later items: no date, a "Later" badge, the same ··· menu. */
export function LaterList({
  kind,
  items,
  loading,
  failed,
  hasMore,
  onMore,
  actions,
  mobile,
}: {
  kind: LaterKind
  items: ScheduleItem[]
  loading: boolean
  failed: boolean
  hasMore: boolean
  onMore: () => void
  actions: QueueActions
  mobile: boolean
}) {
  const { t } = useTranslation('ofQueue')
  return (
    <div className="ofq-later-list">
      {items.map((item) => (
        <QueueCard key={item.id} item={item} actions={actions} mobile={mobile} />
      ))}
      <ListLoading loading={loading} empty={!items.length} />
      {failed && <LoadFailed onRetry={onMore} />}
      {!loading && !failed && !hasMore && !items.length && (
        <div className="ofq-later-empty">
          <FeatherIcon name={kind === 'post' ? 'file-text' : 'message-square'} size={44} />
          <span>{t(`laterEmpty.${kind}`)}</span>
        </div>
      )}
      {hasMore && !failed && <Sentinel onVisible={onMore} token={items.length} />}
    </div>
  )
}

/**
 * The auto-publish switch and interval (`users/settings/post|chat`,
 * `PATCH {"period":N}`, 0 = off). Turning it on can publish a creator's
 * saved posts, so it asks first.
 */
export function AutoPublishPanel({ kind, of, canWrite, say }: { kind: LaterKind; of: OfApi; canWrite: boolean; say: (text: string) => void }) {
  const { t } = useTranslation('ofQueue')
  const [setting, setSetting] = useState<AutoPublish | null>(null)
  const [busy, setBusy] = useState(false)
  const [asking, setAsking] = useState(false)
  const referer = LATER_REFERER[kind]

  useEffect(() => {
    let live = true
    setSetting(null)
    of.get<Record<string, unknown>>(`users/settings/${kind}`, {}, referer)
      .then((raw) => live && setSetting(toAutoPublish(raw)))
      .catch(() => live && setSetting(toAutoPublish(null)))
    return () => {
      live = false
    }
  }, [of, kind, referer])

  const save = useCallback(
    async (next: AutoPublish, done: string) => {
      setBusy(true)
      try {
        await of.send('PATCH', `users/settings/${kind}`, referer, { period: next.enabled ? next.period : 0 })
        setSetting(next)
        say(done)
      } catch (e) {
        say(e instanceof Error && e.message ? e.message : t('error'))
      } finally {
        setBusy(false)
      }
    },
    [of, kind, referer, say, t],
  )

  const labelId = `ofq-every-${kind}`
  return (
    <div className="ofq-auto">
      <div className="ofq-auto-row lib-anchor">
        <span id={`ofq-switch-${kind}`}>{t(`auto.${kind}.switch`)}</span>
        <button
          type="button"
          role="switch"
          aria-checked={!!setting?.enabled}
          aria-labelledby={`ofq-switch-${kind}`}
          className={`ofq-switch ${setting?.enabled ? 'is-on' : ''}`}
          disabled={!canWrite || !setting || busy}
          onClick={() => {
            if (!setting) return
            if (setting.enabled) void save({ ...setting, enabled: false }, t('auto.off'))
            else setAsking(true)
          }}
        >
          <span className="ofq-switch-knob" />
        </button>
        {asking && setting && (
          <ConfirmPopover
            title={t(`auto.${kind}.onTitle`)}
            body={t(`auto.${kind}.onBody`, { h: setting.period })}
            confirmLabel={t('auto.turnOn')}
            onConfirm={() => {
              setAsking(false)
              void save({ ...setting, enabled: true }, t('auto.on'))
            }}
            onCancel={() => setAsking(false)}
          />
        )}
      </div>
      <label className="ofq-auto-field">
        <span id={labelId} className="ofq-auto-label">
          {t(`auto.${kind}.every`)}
        </span>
        <select
          aria-labelledby={labelId}
          className="ofq-select"
          disabled={!canWrite || !setting?.enabled || busy}
          value={setting?.period ?? ''}
          onChange={(e) => setting && void save({ ...setting, period: Number(e.target.value) }, t('auto.changed'))}
        >
          {(setting?.options ?? []).map((h) => (
            <option key={h} value={h}>
              {t(`auto.${kind}.since`, { h })}
            </option>
          ))}
        </select>
      </label>
    </div>
  )
}
